import * as signalR from "@microsoft/signalr";
import { getToken } from "./api";

/**
 * THE MATCH TRANSPORT. Three calls out, one handler per SignalType in.
 *
 * ⭐ **THIS FILE REPLACES `BestHTTPShim`, AND THAT IS THE SINGLE BIGGEST RISK THIS REWRITE
 * RETIRES.** The old client shipped a socket library the browser cannot run
 * (`BestHTTP.dll` uses `System.Net.Sockets`), so we reimplemented 64 of its members by hand. Its
 * failure mode was the worst kind: `ReadyToInitialize` is the only argument-free call, so it kept
 * working - matches started, boards built with 30 cards, the table rendered - and **not one player
 * action ever reached the server**, because `JsonUtility.ToJson` returns `{}` for a primitive and
 * every command argument was silently empty. A SignalR test client passed 26 of 26 against it.
 *
 * Here the browser has a real, first-party SignalR client. There is no shim, no hand-written
 * serialiser and no member we might have missed.
 *
 * ⚠️ **THE SERVER INVOKES A CLIENT METHOD NAMED AFTER THE `SignalType`, WITH ONE JSON STRING.**
 * `GameHub.SenderFor` does `SendAsync(type.ToString(), json)`. So the handler names below are not
 * ours to choose - they are the enum's members (`Revival.Core.decompiled.cs:6728`). An unregistered
 * name is silently discarded by SignalR, which is exactly how the lobby once sat black for hours, so
 * `onAny` logs anything we did not expect rather than dropping it.
 *
 * ⚠️ **THE TOKEN GOES IN THE QUERY STRING AS WELL AS THE HEADER.** `GameHub.CurrentUserId` reads
 * `access_token` from the query FIRST, and a browser WebSocket handshake cannot carry an
 * Authorization header at all. `accessTokenFactory` is how the SignalR client supplies it.
 */
export type SignalName =
  | "Ping"
  | "LobbyUpdate"
  | "GameAction"
  | "GameVisual"
  | "Direct"
  | "LogicEvents"
  | "Information"
  | "TimeOut"
  | "Error"
  | "DropConnection"
  | "Reauthorization"
  | "RefreshToken"
  | "Matched"
  | "VulcaniteRentExpired"
  | "SubscriptionExpired"
  | "RankedDecay";

const SIGNAL_NAMES: SignalName[] = [
  "Ping", "LobbyUpdate", "GameAction", "GameVisual", "Direct", "LogicEvents",
  "Information", "TimeOut", "Error", "DropConnection", "Reauthorization",
  "RefreshToken", "Matched", "VulcaniteRentExpired", "SubscriptionExpired", "RankedDecay",
];

export type SignalHandler = (name: SignalName, payload: unknown, raw: string) => void;

/**
 * ⭐ **THE CONNECTION'S STATE, FOR ANYONE WHO NEEDS TO TELL THE PLAYER.**
 *
 * 🚨 **UNTIL 2026-08-22 A DROPPED CONNECTION WAS A `console.warn` AND NOTHING ELSE.**
 * `withAutomaticReconnect` retries for ~17s and then `onclose` fires; both handlers wrote to the
 * console, and `ui/` had no component that could say "reconnecting". **What the player saw was a
 * board that stopped responding**, which is indistinguishable from the game having frozen - a
 * symptom this project has already lost days to. The ORIGINAL client carried `ReconnectionView`
 * AND `NetworkIssuesView` for exactly this state, so this was a regression, not a design choice.
 *
 * ⛔ **THE NET LAYER STILL OWNS NO DOM.** It publishes a state; `ui/connection.ts` draws it and
 * `main.ts` wires the two. A `document.createElement` in here would make the hub unusable from a
 * test or a harness, and this file is already the one the shim traps live under.
 */
export type ConnState = "connected" | "reconnecting" | "lost";
export type ConnStateHandler = (state: ConnState) => void;

export class GameHubClient {
  private conn: signalR.HubConnection | null = null;
  private handlers: SignalHandler[] = [];

  /** Every signal, in arrival order, for the diagnostic overlay and for tests. */
  readonly received: { at: number; name: SignalName; raw: string }[] = [];

  private stateHandlers: ConnStateHandler[] = [];

  /** Subscribe to connection state. `ui/connection.ts` is the only caller that draws anything. */
  onConnState(h: ConnStateHandler): void {
    this.stateHandlers.push(h);
  }

  /* ⚠️ A HANDLER THAT THROWS MUST NOT TAKE THE CONNECTION WITH IT. These fire from SignalR's own
     callbacks, so an exception here would surface as a hub fault - a UI bug reported as a network
     one, which is the confident-wrong-cause class this repo keeps paying for. */
  private state(s: ConnState): void {
    for (const h of this.stateHandlers) {
      try { h(s); } catch (e) { console.error("[hub] connection-state handler threw", e); }
    }
  }

  onSignal(h: SignalHandler): void {
    this.handlers.push(h);
  }

  async connect(path = "/hubs/game"): Promise<void> {
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(path, {
        accessTokenFactory: () => getToken() ?? "",
      })
      // ⚠️ Reconnect, but do NOT pretend a reconnect is transparent. The server reaps an idle match
      // and a reconnect can legitimately land with no session, which `ReadyToInitialize` reports.
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    for (const name of SIGNAL_NAMES) {
      conn.on(name, (raw: string) => this.dispatch(name, raw));
    }

    /* ⛔ **ALL THREE STATES, NOT JUST THE ONE THAT ENDS.** `onreconnecting` is the ~17s the player
       actually spends staring at a dead board; `onclose` is the end of it. Reporting only `onclose`
       would leave the whole retry window silent, which is the window the complaint comes from. */
    conn.onreconnecting((e) => {
      console.warn("[hub] reconnecting", e);
      this.state("reconnecting");
    });
    conn.onclose((e) => {
      console.warn("[hub] closed", e);
      this.state("lost");
    });
    conn.onreconnected(() => {
      console.info("[hub] reconnected - re-arming the match");
      this.state("connected");
      void this.readyToInitialize();
    });

    await conn.start();
    this.conn = conn;
    console.info("[hub] connected");
  }

  private dispatch(name: SignalName, raw: string): void {
    this.received.push({ at: Date.now(), name, raw });

    let payload: unknown = raw;
    try {
      payload = typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch {
      // ⚠️ NOT AN ERROR. Some signals carry a bare string. Reporting a parse failure as a fault
      // would make every one of them look like a defect.
    }

    for (const h of this.handlers) {
      try {
        h(name, payload, raw);
      } catch (e) {
        // ⛔ ONE HANDLER MUST NOT TAKE DOWN THE TRANSPORT. A throw here would stop every later
        // signal for the whole match, and the symptom would be "the game froze mid-turn".
        console.error(`[hub] handler threw on ${name}`, e);
      }
    }
  }

  /**
   * ⭐ THE FIRST CALL OF EVERY MATCH. The server answers with the opening snapshot; until it does,
   * there is no board. If nothing comes back, the session was reaped or the token did not resolve -
   * both of which the server logs, so check there before touching this file.
   */
  readyToInitialize(): Promise<void> {
    return this.invoke("ReadyToInitialize");
  }

  /**
   * ⭐ THE ONE CALL THE WHOLE GAME IS BUILT AROUND.
   *
   * ⚠️ **`model` IS SERIALISED HERE AND SENT AS A STRING**, matching `GameHub.PerformCommand(string
   * cmdName, string json)`. The old client's equivalent produced `{}` for anything that was not a
   * class and nobody noticed for weeks, so this asserts rather than assumes.
   *
   * ⚠️ **THE ENGINE REJECTS ANY COMMAND WHOSE `TimeHash` IS NOT THE TIMER'S CURRENT HASH**
   * (`Revival.Core.decompiled.cs:16700`). The client does not compute it, it ECHOES the last timer
   * value the server sent. So a "time hash isn't correct" is always a desync we caused, never
   * something to relax.
   */
  async performCommand(cmdName: string, model: unknown): Promise<void> {
    const json = JSON.stringify(model ?? {});
    if (typeof json !== "string" || json === "undefined") {
      throw new Error(
        `[hub] refusing to send ${cmdName} with an unserialisable model. This is the exact ` +
          `failure the old shim had, where every command crossed the wire empty.`,
      );
    }
    await this.invoke("PerformCommand", cmdName, json);
  }

  autoPerformCommand(): Promise<void> {
    return this.invoke("AutoPerformCommand");
  }

  private async invoke(method: string, ...args: unknown[]): Promise<void> {
    if (!this.conn || this.conn.state !== signalR.HubConnectionState.Connected) {
      throw new Error(`[hub] ${method} called while not connected (state ${this.conn?.state})`);
    }
    await this.conn.invoke(method, ...args);
  }

  async stop(): Promise<void> {
    await this.conn?.stop();
    this.conn = null;
  }
}
